import {
  ATTACHMENT_OFFICE_MIME_TYPES,
  type AttachmentOfficeMimeType,
  isAttachmentOfficeMimeType,
} from "@rakazo/contracts";
import { attachmentExtensionForMimeType } from "@rakazo/core";

/** Badge per Office type; typed over the contracts list so a new type is a compile error here. */
const OFFICE_BADGES: Readonly<Record<AttachmentOfficeMimeType, string>> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
};

/**
 * Office documents a bot or the user can attach (RAK-162, shared/rakazo/OFFICE-ANHAENGE.md).
 * They never render inline: the file card downloads them, Word/Excel/PowerPoint open them.
 * Types come from contracts ATTACHMENT_OFFICE_MIME_TYPES, extensions from core.
 */
export const OFFICE_FILE_TYPES = ATTACHMENT_OFFICE_MIME_TYPES.map((mimeType) => ({
  mimeType,
  extension: attachmentExtensionForMimeType(mimeType),
  badge: OFFICE_BADGES[mimeType],
}));

/** "Application/PDF; charset=x" → "application/pdf". */
export function baseMimeType(mimeType: string): string {
  return (mimeType.split(";")[0] ?? "").trim().toLowerCase();
}

/** True for the three Office Open XML types that are only downloaded, never previewed. */
export function isOfficeMimeType(mimeType: string): boolean {
  return isAttachmentOfficeMimeType(baseMimeType(mimeType));
}

/** Short type badge for a file artifact ("DOCX", "PDF", "MD", or the upper-cased subtype). */
export function fileTypeBadge(mimeType: string): string {
  const mime = baseMimeType(mimeType);
  if (isAttachmentOfficeMimeType(mime)) return OFFICE_BADGES[mime];
  if (mime === "text/html") return "HTML";
  if (mime === "text/markdown") return "MD";
  if (mime === "application/pdf") return "PDF";
  const slash = mime.indexOf("/");
  return (slash === -1 ? mime : mime.slice(slash + 1)).toUpperCase().slice(0, 6);
}

/**
 * File name as shown on a card: control and format characters (bidi overrides such as U+202E)
 * become "_" like in core attachmentDownloadName, so the card shows the name the download gets
 * and cannot be mirrored.
 */
export function fileDisplayName(name: string): string {
  return name.replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+/gu, "_");
}

/** Splits a display name so a card can shorten the stem and keep the extension visible. */
export function splitFileName(name: string): { stem: string; extension: string } {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || name.length - dot > 6) return { stem: name, extension: "" };
  return { stem: name.slice(0, dot), extension: name.slice(dot) };
}
